import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import ComponentHeader from '../../components/Headers/component-header';
import InlineQuery from '../../components/InlineQuery';
import List from '../../components/List/List';
import SectionedItems from '../../components/SectionedItems';
import ActorItem from '../../components/SectionedItems/ActorItem';
// import HottestMomentItem from '../../components/SectionedItems/HottestMomentItem';

import {
  formatTrendingActors,
  formatTrendingShows,
  formatTopShows,
  formatAnticipatedShows,
  formatTopShowsBinged
  // formatHomepageShowsHottestMoments
} from '../../utils/homepage';

import './Homepage.css';

const trendingShowsHeaders = [
  { key: 'showTitle', label: 'Title' },
  { key: 'network', label: 'Network' },
  { key: 'percentIncrease', label: '% Growth' }
];

const anticipatedShowsHeader = [
  { key: 'showTitle', label: 'Title' },
  { key: 'network', label: 'Network' },
  { key: 'releaseDate', label: 'Release Date' }
];

const topShowsBingedHeaders = [
  { key: 'showTitle', label: 'Show' },
  { key: 'network', label: 'Network' },
  {
    key: 'bingeRate',
    label: 'Binge Rate',
    tooltip:
      'Percent of viewers who unitarily tracked 4 or more episodes in a given 24 hour period'
  },
  {
    key: 'bingeShare',
    label: 'Share of Binges',
    tooltip: 'The portion of all Binge sessions belonging to a specific show'
  }
];

const topShowsHeaders = [
  { key: 'showTitle', label: 'Show' },
  { key: 'network', label: 'Network' },
  {
    key: 'viewShare',
    label: 'Share of Views',
    tooltip:
      'The portion of all Views, for selected filter criteria, belonging to a specific piece of content'
  },
  {
    key: 'viewerShare',
    label: 'Share of Viewers',
    tooltip:
      'Share of Viewers measures how many people are in an audience (those who view a specific piece of content) compared to the total audience of the app. It is important to note that the users are not mutually exclusive in this calculation, and therefore the sum can total greater than 100%'
  }
];

const HomePageShows = ({ queries, formatFiltersForURL }) => {
  const [toggled, setToggled] = useState('watched');

  const toggleView = e => {
    setToggled(e.target.value);
  };

  return (
    <>
      <Row className="tvt-homepage__row">
        <Col md={12}>
          <ComponentHeader
            title="Top Performing Titles"
            tooltip="These reports highlight the most watched and binged series in the past 7 completed days"
          />
          <div className="tvt-homepage__chart-header">
            <div className="top-performing-titles-toggle">
              <button
                className={toggled === 'watched' ? 'active' : 'inactive'}
                value="watched"
                onClick={toggleView}
              >
                Watched
              </button>
              |
              <button
                className={toggled === 'binged' ? 'active' : 'inactive'}
                value="binged"
                onClick={toggleView}
              >
                Binged
              </button>
            </div>
            <Link
              to={
                toggled === 'watched'
                  ? `/shows?${formatFiltersForURL(true)}`
                  : `/report/binge?${formatFiltersForURL(true)}`
              }
              className="tvt-homepage__see-all-link"
            >
              SEE ALL
            </Link>
          </div>

          {toggled === 'watched' && (
            <InlineQuery query={queries.topPerformingShows}>
              {query => (
                <div className="tvt-homepage__top-titles">
                  <List
                    headers={topShowsHeaders}
                    data={formatTopShows(query)}
                    bodyClass="tvt-report-list__body--no-padding"
                  />
                </div>
              )}
            </InlineQuery>
          )}
          {toggled === 'binged' && (
            <InlineQuery query={queries.topPerformingShowsBinge}>
              {query => (
                <div className="tvt-homepage__top-titles">
                  <List
                    headers={topShowsBingedHeaders}
                    data={formatTopShowsBinged(query)}
                    bodyClass="tvt-report-list__body--no-padding"
                  />
                </div>
              )}
            </InlineQuery>
          )}
        </Col>
      </Row>
      <Row className="tvt-homepage__row">
        <Col md={6}>
          <div className="tvt-homepage__chart-header">
            <ComponentHeader
              title="Anticipated Titles"
              tooltip="This report showcases the top 10 most followed unreleased series on the TV Time app"
            />

            <Link
              className="tvt-homepage__see-all-link"
              to={`/shows/anticipation-report?${formatFiltersForURL()}`}
            >
              SEE ALL
            </Link>
          </div>

          <InlineQuery query={queries.homepageShowAnticipation}>
            {query => (
              <div className="tvt-homepage__anticipated-titles">
                <List
                  headers={anticipatedShowsHeader}
                  data={formatAnticipatedShows(query)}
                  bodyClass="tvt-report-list__body--no-padding"
                />
              </div>
            )}
          </InlineQuery>
        </Col>
        <Col md={6}>
          <div className="tvt-homepage__chart-header">
            <ComponentHeader
              title="Titles on the Rise"
              tooltip="This report highlights the top 10 series by follower growth in the past 7 completed days"
            />
          </div>
          <InlineQuery query={queries.trendingShows}>
            {query => (
              <div className="tvt-homepage__trending-titles">
                <List
                  headers={trendingShowsHeaders}
                  data={formatTrendingShows(query)}
                  bodyClass="tvt-report-list__body--no-padding"
                />
              </div>
            )}
          </InlineQuery>
        </Col>
      </Row>
      {/* <Row>
        <Col md={12}>
          <ComponentHeader title="Hottest Moments This Week" />
          <InlineQuery query={queries.homepageShowsHottestMoments}>
            {query => (
              <SectionedItems
                list={formatHomepageShowsHottestMoments(query)}
                itemType={HottestMomentItem}
                hasSeparators={false}
              />
            )}
          </InlineQuery>
        </Col>
      </Row> */}

      <Row className="tvt-homepage__row">
        <Col md={12}>
          <div className="tvt-homepage__chart-header">
            <ComponentHeader
              title="Trending Talent: New Fan Votes this Week"
              tooltip="This report highlights the top performing series characters by total character votes in the past 7 completed days"
            />
            <Link
              className="tvt-homepage__see-all-link"
              to={`/actors?${formatFiltersForURL(true)}`}
            >
              SEE ALL
            </Link>
          </div>

          <InlineQuery query={queries.trendingActors}>
            {query => (
              <SectionedItems
                list={formatTrendingActors(query)}
                itemType={ActorItem}
                hasSeparators={false}
              />
            )}
          </InlineQuery>
        </Col>
      </Row>
    </>
  );
};

export default HomePageShows;

HomePageShows.propTypes = {
  queries: PropTypes.shape({
    trendingActors: PropTypes.shape({}),
    trendingShows: PropTypes.shape({}),
    topPerformingShows: PropTypes.shape({}),
    topPerformingShowsBinge: PropTypes.shape({}),
    homepageShowAnticipation: PropTypes.shape({}),
    homepageShowsHottestMoments: PropTypes.shape({})
  }).isRequired,
  formatFiltersForURL: PropTypes.func.isRequired
};
